import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  RotateCcw,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type { LocationTableRow, LocationStatus } from '@/types/locations'

/** Human-readable status labels */
const STATUS_LABELS: Record<LocationStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  coming_soon: 'Coming Soon',
}

/** All location statuses */
const LOCATION_STATUSES: LocationStatus[] = ['active', 'inactive', 'coming_soon']

interface LocationsTableProps {
  /** Location data to display */
  data: LocationTableRow[]
  /** Loading state */
  isLoading: boolean
  /** Callback when edit action is triggered */
  onEdit: (id: string) => void
  /** Callback when delete action is triggered */
  onDelete: (id: string, name: string) => void
  /** Callback when restore action is triggered */
  onRestore?: (id: string) => void
}

/** Badge variant mapping for location status */
const statusVariants: Record<LocationStatus, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  inactive: 'secondary',
  coming_soon: 'outline',
}

/** Status filter options with icons */
const statusFilterOptions = LOCATION_STATUSES.map((status) => ({
  label: STATUS_LABELS[status],
  value: status,
  icon: status === 'active' ? CheckCircle : status === 'coming_soon' ? Clock : XCircle,
}))

export function LocationsTable({
  data,
  isLoading,
  onEdit,
  onDelete,
  onRestore,
}: LocationsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<LocationTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <div className={cn('font-medium', row.original.deleted_at && 'line-through text-muted-foreground')}>
          {row.getValue('name')}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'airport_code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Airport Code" />
      ),
      cell: ({ row }) => {
        const code = row.getValue('airport_code') as string | null
        return code ? (
          <span className="font-mono text-sm">{code}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: 'location_code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Location Code" />
      ),
      cell: ({ row }) => {
        const code = row.getValue('location_code') as string
        return <span className="font-mono text-sm">{code}</span>
      },
    },
    {
      accessorKey: 'city',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Location" />
      ),
      cell: ({ row }) => {
        const city = row.getValue('city') as string | null
        const state = row.original.state_province
        if (!city && !state) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <span>
            {city}
            {city && state ? ', ' : ''}
            {state}
          </span>
        )
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as LocationStatus | null
        if (!status) return null
        return (
          <Badge variant={statusVariants[status] || 'default'}>
            {STATUS_LABELS[status] || status}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const isDeleted = !!row.original.deleted_at
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isDeleted ? (
                <DropdownMenuItem onClick={() => onRestore?.(row.original.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDelete(row.original.id, row.original.name)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const name = (row.original.name || '').toLowerCase()
      const airportCode = (row.original.airport_code || '').toLowerCase()
      const locationCode = (row.original.location_code || '').toLowerCase()
      const city = (row.original.city || '').toLowerCase()
      return (
        name.includes(search) ||
        airportCode.includes(search) ||
        locationCode.includes(search) ||
        city.includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = ['Name', 'Airport Code', 'Location Code', 'City', 'State', 'Status']
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.name,
      row.original.airport_code || '',
      row.original.location_code || '',
      row.original.city || '',
      row.original.state_province || '',
      row.original.status ? STATUS_LABELS[row.original.status as LocationStatus] : '',
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `locations_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search locations..."
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={statusFilterOptions}
        />
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading locations...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(row.original.deleted_at && 'opacity-50')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No locations found. Add your first location to get started.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Enhanced Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
